/// <reference types="vite/client" />

import { createApiClient, isApiError, getApiError, type ApiClient } from '@aia/platform-core';

export { isApiError, getApiError };
export type { PaginationMeta as Pagination } from '@aia/platform-core';

const TOKEN_KEY = 'pilotpulse_token';
const USER_KEY = 'pilotpulse_user';
const E2E_MODE_KEY = 'e2e_test_mode';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const baseClient = createApiClient({ baseUrl: API_BASE_URL });

function isE2EMode(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(E2E_MODE_KEY) === 'true';
}

export function enableE2EMode(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(E2E_MODE_KEY, 'true');
}

function authHeaders(): Record<string, string> {
  const token =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY)
      : null;
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (isE2EMode()) {
    headers['X-E2E-Bypass'] = 'true';
  }
  return headers;
}

const AUTH_PAGES = [
  '/login',
  '/register',
  '/accept-invite',
  '/forgot-password',
  '/reset-password',
  '/sso',
];

function handleUnauthorized(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  if (!AUTH_PAGES.some((p) => window.location.pathname.startsWith(p))) {
    window.location.href = '/login?session_expired=true';
  }
}

interface SkipAuthOptions {
  skipAuthRedirect?: boolean;
}

async function withAuth<T>(fn: () => Promise<T>, opts?: SkipAuthOptions): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!opts?.skipAuthRedirect && isApiError(err) && err.status === 401) {
      handleUnauthorized();
    }
    throw err;
  }
}

export const api: ApiClient = {
  get: (url, opts) =>
    withAuth(() =>
      baseClient.get(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  post: (url, body, opts) =>
    withAuth(() =>
      baseClient.post(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  put: (url, body, opts) =>
    withAuth(() =>
      baseClient.put(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  patch: (url, body, opts) =>
    withAuth(() =>
      baseClient.patch(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  delete: (url, opts) =>
    withAuth(() =>
      baseClient.delete(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
};

/**
 * Make a request that skips the automatic 401→login redirect.
 * Used for auth-related calls (login, register, verify token).
 */
export const authApi: ApiClient = {
  get: (url, opts) =>
    withAuth(
      () => baseClient.get(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  post: (url, body, opts) =>
    withAuth(
      () =>
        baseClient.post(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  put: (url, body, opts) =>
    withAuth(
      () => baseClient.put(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  patch: (url, body, opts) =>
    withAuth(
      () =>
        baseClient.patch(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  delete: (url, opts) =>
    withAuth(
      () => baseClient.delete(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
};

// ==========================================
// Team Management API Functions
// ==========================================

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'OrgAdmin' | 'UnitAdmin' | 'Instructor';
  title?: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  organizationName?: string;
  orgUnits: { id: string; name: string }[];
  pilots: { id: string; name: string }[];
  isLocked?: boolean;
  isPermanentlyLocked?: boolean;
  lockedUntil?: string;
  failedLoginAttempts?: number;
  lockoutCount?: number;
  invitedById?: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  name?: string;
  role: 'OrgAdmin' | 'UnitAdmin' | 'Instructor';
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invitedBy: { id: string; name: string; email: string } | null;
  pilots: { id: string; name: string }[];
  orgUnits: { id: string; name: string }[];
  message?: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
  cancelledAt?: string;
  resendCount: number;
  lastResentAt?: string;
}

export interface ListUsersParams {
  role?: string;
  status?: string;
  search?: string;
  orgUnitId?: string;
  page?: number;
  limit?: number;
}

export interface ListInvitationsParams {
  status?: string;
  role?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface SendInvitationData {
  email: string;
  name?: string;
  role: 'OrgAdmin' | 'UnitAdmin' | 'Instructor';
  orgUnitIds?: string[];
  pilotIds?: string[];
  message?: string;
  overrideSeatLimit?: boolean;
}

export interface ListUsersPayload {
  users: TeamMember[];
}

export interface ListInvitationsPayload {
  invitations: TeamInvitation[];
}

export interface SendInvitationPayload {
  seatLimitExceeded?: boolean;
}

export const teamApi = {
  listUsers: async (params?: ListUsersParams) => {
    const queryParams = new URLSearchParams();
    if (params?.role) queryParams.set('role', params.role);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.orgUnitId) queryParams.set('orgUnitId', params.orgUnitId);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));

    const queryString = queryParams.toString();
    return api.get<ListUsersPayload>(`/users${queryString ? `?${queryString}` : ''}`);
  },

  listInvitations: async (params?: ListInvitationsParams) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.role) queryParams.set('role', params.role);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));

    const queryString = queryParams.toString();
    return api.get<ListInvitationsPayload>(`/invitations${queryString ? `?${queryString}` : ''}`);
  },

  sendInvitation: async (data: SendInvitationData) => {
    return api.post<SendInvitationPayload>('/invitations', data);
  },

  cancelInvitation: async (id: string) => {
    return api.delete(`/invitations/${id}`);
  },

  resendInvitation: async (id: string) => {
    return api.post(`/invitations/${id}/resend`);
  },
};
